import Keycloak from "keycloak-js";
import { CurrentUser, UserTeam, fetchCurrentUser } from "../api/meApi";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface AuthContextValue {
  authenticated: boolean;
  username?: string;
  token?: string;
  /** Realm and client roles from the access token. */
  roles: string[];
  /** Answered by the server; undefined until it responds. */
  isAdmin?: boolean;
  /** Realm roles the server accepts, so a denial can name what is missing. */
  adminRoles?: string[];
  /** The signed-in user's team; undefined while loading, null when they have none. */
  team?: UserTeam | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  authenticated: false,
  roles: [],
  logout: () => undefined,
});

export const useAuth = () => useContext(AuthContext);

/** Realm roles plus the roles of every client in the token. */
function rolesFromToken(parsed: Keycloak["tokenParsed"]): string[] {
  const roles = new Set<string>(parsed?.realm_access?.roles ?? []);
  for (const client of Object.values(parsed?.resource_access ?? {})) {
    for (const role of client?.roles ?? []) roles.add(role);
  }
  return [...roles];
}

let keycloakInstance: Keycloak | null = null;

/**
 * Keycloak registers the dev redirect URIs by the literal spelling
 * `http://localhost:<port>`, so opening the app on 127.0.0.1 or the machine's
 * LAN address -- both of which CRA prints on startup -- fails the login with
 * "Invalid parameter: redirect_uri" before anything renders. keycloak-js
 * derives redirect_uri from window.location, so the only way to fix it in the
 * app is to be on the canonical host before init() runs.
 *
 * Returns null for localhost and for every deployed hostname, which are
 * registered under their own names and must not be rewritten.
 */
export function canonicalDevUrl(location: {
  hostname: string;
  protocol: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  const { hostname, protocol, port, pathname, search, hash } = location;
  if (hostname === "localhost") return null;

  const loopback =
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname === "::1" ||
    hostname === "[::1]";
  // RFC1918 ranges only: a public hostname is a real deployment.
  const privateLan =
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname);

  if (!loopback && !privateLan) return null;
  return `${protocol}//localhost${port ? `:${port}` : ""}${pathname}${search}${hash}`;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState<string>();
  const [token, setToken] = useState<string>();
  const [roles, setRoles] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean>();
  const [adminRoles, setAdminRoles] = useState<string[]>();
  const [team, setTeam] = useState<UserTeam | null>();
  const initStarted = useRef(false);

  useEffect(() => {
    if (initStarted.current) {
      return;
    }
    const canonical = canonicalDevUrl(window.location);
    if (canonical) {
      // replace(), not assign(): the un-canonical URL must not stay in history
      // or Back lands straight back on the broken login.
      window.location.replace(canonical);
      return;
    }
    initStarted.current = true;

    if (!keycloakInstance) {
      keycloakInstance = new Keycloak("/keycloak.json");
    }
    keycloakInstance
      .init({ onLoad: "login-required", checkLoginIframe: false })
      .then((isAuthenticated) => {
        setAuthenticated(isAuthenticated);
        setUsername(
          keycloakInstance?.tokenParsed?.preferred_username as
            | string
            | undefined,
        );
        setToken(keycloakInstance?.token);
        setRoles(rolesFromToken(keycloakInstance?.tokenParsed));
      })
      .catch((err) => {
        console.error("Keycloak init failed", err);
      });
  }, []);

  useEffect(() => {
    if (!authenticated || !keycloakInstance) return;
    const refresh = window.setInterval(() => {
      keycloakInstance
        ?.updateToken(60)
        .then(() => {
          setToken(keycloakInstance?.token);
          setRoles(rolesFromToken(keycloakInstance?.tokenParsed));
        })
        .catch((err) => console.error("Keycloak token refresh failed", err));
    }, 30_000);
    return () => window.clearInterval(refresh);
  }, [authenticated]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    fetch(`${process.env.REACT_APP_API_URL ?? ""}/api/v1/admin/context`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : { isAdmin: false }))
      .then((body) => {
        if (cancelled) return;
        setIsAdmin(Boolean(body?.isAdmin));
        setAdminRoles(
          Array.isArray(body?.adminRoles)
            ? body.adminRoles.filter((role: unknown): role is string => typeof role === "string")
            : undefined,
        );
      })
      // A failed probe must never promote anyone; the server's own 403 is the
      // real enforcement and this only decides what is shown.
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      });

    return () => {
      cancelled = true;
    };
    // Only the first token matters; refreshes carry the same claims.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(token)]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    fetchCurrentUser(token)
      .then((user: CurrentUser) => {
        if (!cancelled) setTeam(user.team);
      })
      // Resolve to "no team" rather than retrying: this only labels the
      // profile menu, and the screens enforce access on their own.
      .catch(() => {
        if (!cancelled) setTeam(null);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(token)]);

  const logout = () => {
    keycloakInstance?.logout({ redirectUri: window.location.origin });
  };

  return (
    <AuthContext.Provider
      value={{ authenticated, username, token, roles, isAdmin, adminRoles, team, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
